import {
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";

import type { RootState } from "../../app/store";
import { safeLocalStorage } from "../utils/utils";
import { SHARED_SESSION_ID_KEY } from "./constants";
import type { SharedLocationSession } from "./types";

export enum SharingStatus {
  STOPPED = "STOPPED",
  SHARING = "SHARING",
  LOADING = "LOADING",
  COMPLETED = "COMPLETED",
}

export enum ReceivedLocationStatus {
  ONGOING = "ONGOING",
  UNAVAILABLE = "UNAVAILABLE",
  UNSET = "UNSET",
}

export interface ShareLocationState {
  shareModalOpen: boolean; // user is not still sharing
  sharedLocationModalOpen: boolean; // user is already sharing
  sharingStatus: SharingStatus;
  sessionId: string | null;
  expirationTimestamp: number | null;
  sharedLocationReceived: SharedLocationSession | null;
  receivedSessionId: string | null;
  receivedLocationStatus: ReceivedLocationStatus;
}

export const initialState: ShareLocationState = {
  expirationTimestamp: null,
  receivedLocationStatus: ReceivedLocationStatus.UNSET,
  receivedSessionId: null,
  sessionId: null,
  sharedLocationModalOpen: false,
  sharedLocationReceived: null,
  shareModalOpen: false,
  sharingStatus: SharingStatus.STOPPED,
};

export const shareLocationSlice = createSlice({
  initialState,
  name: "shareLocation",
  reducers: {
    resetReceivedSharedLocation: (state) => {
      state.sharedLocationReceived = initialState.sharedLocationReceived;
      state.receivedSessionId = initialState.receivedSessionId;
      state.sharedLocationModalOpen = initialState.sharedLocationModalOpen;
      state.receivedLocationStatus = initialState.receivedLocationStatus;
      safeLocalStorage.removeItem(SHARED_SESSION_ID_KEY);
    },
    resetSharedLocation: (state) => {
      state.sharingStatus = initialState.sharingStatus;
      state.expirationTimestamp = initialState.expirationTimestamp;
      state.sessionId = initialState.sessionId;
    },
    setExpirationTimestamp: (state, action: PayloadAction<number | null>) => {
      state.expirationTimestamp = action.payload;
    },
    setReceivedLocationStatus: (
      state,
      action: PayloadAction<ReceivedLocationStatus>,
    ) => {
      state.receivedLocationStatus = action.payload;
    },
    setReceivedSessionId: (state, action: PayloadAction<string | null>) => {
      state.receivedSessionId = action.payload;
      safeLocalStorage.setItem(SHARED_SESSION_ID_KEY, action.payload);
    },
    setReceivedSharedLocation: (
      state,
      action: PayloadAction<ShareLocationState["sharedLocationReceived"]>,
    ) => {
      state.sharedLocationReceived = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string | null>) => {
      state.sessionId = action.payload;
    },
    setSharedLocationModalOpen: (state, action: PayloadAction<boolean>) => {
      state.sharedLocationModalOpen = action.payload;
    },
    setSharedLocationStatus: (state, action: PayloadAction<SharingStatus>) => {
      state.sharingStatus = action.payload;
    },
    setShareLocationModalOpen: (state, action: PayloadAction<boolean>) => {
      state.shareModalOpen = action.payload;
    },
  },
});

// Selectors
const selectSlice = (state: RootState) => state.shareLocation;

export const selectSharingStatus = createSelector(
  selectSlice,
  (state) => state.sharingStatus,
);

export const selectShareSessionId = createSelector(
  selectSlice,
  (state) => state.sessionId,
);

export const selectExpirationTimestamp = createSelector(
  selectSlice,
  (state) => state.expirationTimestamp,
);

export const selectShareLocationModalOpen = createSelector(
  selectSlice,
  (state) => state.shareModalOpen,
);

export const selectShareModalSenderOpen = createSelector(
  selectSlice,
  (state) => state.sharedLocationModalOpen,
);

export const selectReceivedSharedLocation = createSelector(
  selectSlice,
  (state) => state.sharedLocationReceived,
);

export const selectReceivedSessionId = createSelector(
  selectSlice,
  (state) => state.receivedSessionId,
);

export const selectReceivedLocationStatus = createSelector(
  selectSlice,
  (state) => state.receivedLocationStatus,
);

export const {
  resetReceivedSharedLocation,
  resetSharedLocation,
  setSharedLocationStatus,
  setExpirationTimestamp,
  setSessionId,
  setSharedLocationModalOpen,
  setReceivedSharedLocation,
  setShareLocationModalOpen,
  setReceivedSessionId,
  setReceivedLocationStatus,
} = shareLocationSlice.actions;

export default shareLocationSlice.reducer;
